import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { fmt } from '../lib/helpers';
import { invokeAuth0EdgeFunction } from '../lib/edgeFunctions';
import MetricCard from '../components/MetricCard';
import Loader from '../components/Loader';
import Badge from '../components/Badge';

function formatTokenCount(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

export default function PlatformCompanies() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [companies, setCompanies] = useState([]);
  const [usageByOrg, setUsageByOrg] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companiesData, usageData] = await Promise.all([
        invokeAuth0EdgeFunction('platform-console', { action: 'companies' }, getAccessTokenSilently),
        invokeAuth0EdgeFunction('platform-console', { action: 'ai_usage_30d' }, getAccessTokenSilently),
      ]);
      setCompanies(Array.isArray(companiesData?.companies) ? companiesData.companies : []);
      const map = {};
      for (const row of usageData?.usage ?? []) {
        map[row.organisation_id] = row;
      }
      setUsageByOrg(map);
    } catch (e) {
      setError(e?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  const totalUsers = companies.reduce((sum, c) => sum + (c.total_users || 0), 0);
  const totalActive = companies.reduce((sum, c) => sum + (c.active_users || 0), 0);
  const totalPending = companies.reduce((sum, c) => sum + (c.pending_invites || 0), 0);
  const totalTokens30d = useMemo(
    () => Object.values(usageByOrg).reduce(
      (sum, u) => sum + (u.input_tokens || 0) + (u.output_tokens || 0),
      0,
    ),
    [usageByOrg],
  );

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tbtitle">Companies</div>
          <div className="tbcrumb">Every organisation on Transformax, across the private beta</div>
        </div>
      </div>

      <div className="content">
        <div className="m4">
          <MetricCard label="Companies" value={companies.length} sub="registered organisations" />
          <MetricCard label="Total users" value={totalUsers} sub="across all companies" />
          <MetricCard label="Active" value={totalActive} sub="linked to an account" valueColor={totalActive > 0 ? '#534AB7' : undefined} />
          <MetricCard label="Pending invites" value={totalPending} sub="not yet accepted" />
          <MetricCard label="AI tokens (30d)" value={formatTokenCount(totalTokens30d)} sub="all companies combined" />
        </div>

        {error && <div className="form-error" style={{ margin: '0 0 16px' }}>{error}</div>}

        <div className="card">
          <div className="chdr">
            <span className="ctitle">All companies</span>
          </div>

          {loading ? <Loader /> : companies.length === 0 ? (
            <div className="empty">No companies yet.</div>
          ) : (
            <div>
              <div className="ua-header-row">
                <span style={{ flex: 1.6 }}>Company</span>
                <span style={{ flex: 0.7 }}>Plan</span>
                <span style={{ flex: 0.7 }}>Total users</span>
                <span style={{ flex: 0.7 }}>Active</span>
                <span style={{ flex: 0.8 }}>Pending invites</span>
                <span style={{ flex: 0.9 }}>Tokens (30d)</span>
                <span style={{ flex: 0.9 }}>Created</span>
              </div>
              {companies.map((c) => {
                const usage = usageByOrg[c.id];
                const tokens = (usage?.input_tokens || 0) + (usage?.output_tokens || 0);
                return (
                  <div
                    key={c.id}
                    className="ua-row"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/platform/companies/${c.id}`)}
                  >
                    <div style={{ flex: 1.6, minWidth: 0 }}>
                      <div className="ua-name">
                        {c.name || '—'}
                        {c.is_suspended && <Badge variant="">Suspended</Badge>}
                      </div>
                    </div>
                    <div style={{ flex: 0.7 }}>
                      <span className="ua-meta">{c.plan_tier || 'trial'}</span>
                    </div>
                    <div style={{ flex: 0.7 }}>
                      <span className="ua-meta">{c.total_users}</span>
                    </div>
                    <div style={{ flex: 0.7 }}>
                      <span className="ua-meta">{c.active_users}</span>
                    </div>
                    <div style={{ flex: 0.8 }}>
                      <span className="ua-meta">{c.pending_invites}</span>
                    </div>
                    <div style={{ flex: 0.9 }}>
                      <span className="ua-meta" title={usage ? `${usage.call_count} AI call(s)` : 'No AI usage yet'}>
                        {formatTokenCount(tokens)}
                      </span>
                    </div>
                    <div style={{ flex: 0.9 }}>
                      <span className="ua-meta">{fmt(c.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
